import { useEffect, useRef, useState } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import Player from 'video.js/dist/types/player'
import { captureException } from '@sentry/nextjs'

import './HomVjs'

import { UseVideojsPlayerProps } from '~/components/VideoPlayer/VideoPlayer.types'
import { addChapterMarkers, addPlayerEvents, creditableWatchSeconds } from '~/components/VideoPlayer/VideoPlayer.util'
import { defaultVideoOptions } from '~/components/VideoPlayer/VideoPlayer.const'

import setupVideoAnalyticsPlugin from './video-analytics.plugin'

export default function useVideojsPlayer(props: UseVideojsPlayerProps): void {
  const playerRef = useRef<Player>(null)
  const onEndRef = useRef(props.onEnd)
  const onPlayRef = useRef(props.onPlay)
  const onPauseRef = useRef(props.onPause)
  const onSeekRef = useRef(props.onSeek)

  const timerRef = useRef(null)
  const lastPlayTimeRef = useRef(0)
  // When the watch timer last sampled, so a jump can be checked against the real time available for it.
  const lastSampleAtRef = useRef(Date.now())
  const [isTimerStarted, setIsTimerStarted] = useState(false)

  const {
    options,
    onEnd,
    onPlay,
    onPause,
    onSeek,
    videoRef,
    contextMenu = false,
    videoCategory,
    enableAnalytics,
    setPlayTime,
    setVideoLength,
  } = props

  onEndRef.current = onEnd
  onPlayRef.current = onPlay
  onPauseRef.current = onPause
  onSeekRef.current = onSeek

  useEffect(() => {
    if (videoRef.current) {
      const videoElement = videoRef.current

      const handleError = (): void => {
        const mediaError = videoElement.error
        captureException(new Error('Video loading error'), {
          tags: { feature: 'video', action: 'load-error' },
          extra: {
            code: mediaError?.code,
            message: mediaError?.message,
            src: options.sources[0]?.src,
          },
        })
      }
      videoElement.addEventListener('error', handleError)

      return () => {
        videoElement.removeEventListener('error', handleError)
      }
    }
  }, [options.sources, videoRef])

  // The duration is wanted as early as possible: the watch gate divides by it. Reading it is cheap, so every
  // event that could carry it and every tick of the play timer calls this, and the first one to see a real
  // number wins. The media element is asked first — `player.duration()` only reads a cache video.js fills from
  // its own durationchange handler, so a metadata event that landed before the player was wired up leaves it
  // NaN, which is exactly the case that left videos permanently uncompletable.
  const applyDuration = (): void => {
    const duration = videoRef.current?.duration || playerRef.current?.duration()
    if (duration && Number.isFinite(duration)) setVideoLength(Math.floor(duration))
  }

  // Credits the content seconds covered since the last sample. Sampling the position rather than counting whole
  // ticks means a pause, a rate change or the final part-second all keep their credit.
  //
  // `seeked` moving lastPlayTimeRef is NOT enough on its own to keep a seek uncredited: dragging the scrubber
  // takes longer than the one-second sample and video.js moves currentTime during the drag, so a sample lands
  // mid-drag and reads the whole jump as elapsed. Clamping is what actually holds the line — no single sample
  // can credit more than a sample's worth of watching, however far the position moved.
  const creditElapsed = (): void => {
    const now = Date.now()
    const secondsPassed = (now - lastSampleAtRef.current) / 1000
    lastSampleAtRef.current = now
    const currentTime = playerRef.current?.currentTime()
    if (currentTime != null && currentTime > lastPlayTimeRef.current) {
      const positionMoved = currentTime - lastPlayTimeRef.current
      lastPlayTimeRef.current = currentTime
      setPlayTime((prevTime) => prevTime + creditableWatchSeconds(positionMoved, secondsPassed))
    }
  }

  const startTimer = (): void => {
    if (!timerRef.current) {
      // Restart the clock with the timer: the gap since the last sample was spent paused, and
      // carrying it forward would bankroll a seek made right after pressing play.
      lastSampleAtRef.current = Date.now()
      timerRef.current = setInterval(() => {
        applyDuration()
        creditElapsed()
      }, 1000)
    }
  }

  const stopTimer = (): void => {
    creditElapsed()
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const player = (playerRef.current = videojs(videoRef?.current, { ...defaultVideoOptions, ...options }, () => {
        // Hide overlapping elements on fullscreen mode in safari
        if (videojs.browser.IS_SAFARI) {
          player.on('fullscreenchange', () => {
            const elements = Array.from(document.getElementsByClassName('hide-on-fullscreen'))
            const isFullscreen = player.isFullscreen()
            elements.forEach((element) => {
              if (isFullscreen) element.classList.add('hidden')
              else element.classList.remove('hidden')
            })
          })
        }

        setTimeout(() => {
          if (!player.paused() && !isTimerStarted) {
            setIsTimerStarted(true)
            startTimer()
          }
        }, 200)

        player.on('loadedmetadata', () => {
          addChapterMarkers(player, videoRef.current as HTMLVideoElement)
          applyDuration()
        })
        player.on('durationchange', applyDuration)
        applyDuration()

        // Disable right click options (contextmenu)
        if (!contextMenu) player.on('contextmenu', (e: videojs.EventTarget) => e.preventDefault())

        const handleSeeked = () => {
          lastPlayTimeRef.current = player.currentTime()
        }

        // Register the event-listners

        player.on('ended', () => {
          stopTimer()
          if (onEndRef.current) {
            onEndRef.current()
          }
        })

        player.on('play', (e) => {
          setIsTimerStarted(true)
          startTimer()
          if (onPlayRef.current) {
            onPlayRef.current(e)
          }
        })

        player.on('pause', () => {
          stopTimer()
          if (onPauseRef.current) {
            onPauseRef.current()
          }
        })

        player.on('playing', (e) => {
          if (isTimerStarted) return
          setIsTimerStarted(true)
          startTimer()
          if (onPlayRef.current) {
            onPlayRef.current(e)
          }
        })

        player.on('seeked', () => {
          handleSeeked()
          if (onSeekRef.current) {
            onSeekRef.current()
          }
        })

        addPlayerEvents(player)
      }))

      player.HomVjs({ qualityMenu: true })

      // remove chapter menu
      const chaptersButton = document.getElementsByClassName('vjs-chapters-button')
      if (chaptersButton.length > 0) {
        chaptersButton[0].remove()
      }

      if (enableAnalytics) {
        setupVideoAnalyticsPlugin(player, videoCategory)
      }

      return () => {
        stopTimer()
      }
    } else {
      const player = playerRef.current
      const isSameSrc = player.currentSrc() === options.sources[0].src
      if (!isSameSrc) {
        player.autoplay(options.autoplay)
        player.src(options.sources)
      }
      player.poster(options.poster)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, options, videoRef, enableAnalytics, videoCategory, setPlayTime, setVideoLength])
}
